/**
 * usePageVisibility — returns `true` when the document is currently visible.
 *
 * Used to pause polling timers (`setInterval` for last-sync fetch, etc.)
 * when the user puts the app in the background.  On iOS Safari this matters
 * a lot — backgrounded tabs are aggressively throttled, and our intervals
 * pile up unnecessary work when the tab comes back.  By gating intervals
 * on this flag we get cleaner foreground behaviour AND avoid burst-firing
 * 30 stale callbacks the moment the tab re-activates.
 */
import { useEffect, useState } from 'react';

const getVisibility = () => {
  if (typeof document === 'undefined') return true;
  return !document.hidden;
};

export default function usePageVisibility() {
  const [visible, setVisible] = useState(getVisibility);

  useEffect(() => {
    const onChange = () => setVisible(getVisibility());
    document.addEventListener('visibilitychange', onChange);
    // Also listen for `pageshow` (iOS Safari fires this on bfcache restore)
    window.addEventListener('pageshow', onChange);
    window.addEventListener('focus', onChange);
    return () => {
      document.removeEventListener('visibilitychange', onChange);
      window.removeEventListener('pageshow', onChange);
      window.removeEventListener('focus', onChange);
    };
  }, []);

  return visible;
}
